import clsx from 'clsx';
import * as React from 'react';
import { useInteractOutside, useObjectRef } from 'react-aria';

import { selectInline } from '@gitbook/doc-core';
import { findDOMNode, type InlineProps } from '@gitbook/slate-react';
import { shouldCloseOnInteractOutsideOverlay } from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import { InlineEditingContext } from './InlineEditingContext';

/**
 * Editable annotation.
 */
export function useInlineEdit(
    props: Pick<InlineProps, 'node' | 'editor'> & {
        isOpen: boolean;
        onOpenChange: (isOpen: boolean) => void;
        onEdit: (isEditing: boolean) => void;
        onCancel?: () => void;
        isEditing: boolean;
    }
) {
    const { node, editor, isOpen, onOpenChange, onEdit, onCancel, isEditing } = props;

    const onExitedRef = React.useRef<(() => void) | null>(null);

    const focusEndOfInline = () => {
        editor.change((change) => {
            selectInline(change, node);
            change.collapseToEndOf(node);
            change.collapseToStartOfNextText();
        });
    };

    const cancel = (args: { focusEndOfInline: boolean }) => {
        onExitedRef.current = () => {
            onCancel?.();
            onEdit(false);
        };
        onOpenChange(false);
        if (args.focusEndOfInline) {
            focusEndOfInline();
        }
    };

    const submit = () => {
        onExitedRef.current = () => {
            onEdit(false);
        };
        onOpenChange(false);
        focusEndOfInline();
    };

    const edit = () => {
        onOpenChange(true);
        onEdit(true);
    };

    const popoverRef = useObjectRef<HTMLDivElement>(null);
    useInteractOutside({
        ref: popoverRef,
        isDisabled: !isEditing || !isOpen,
        onInteractOutside(event) {
            // First fast check if we can close with overlay rules.
            if (
                !(event.target instanceof Element) ||
                !shouldCloseOnInteractOutsideOverlay(event.target)
            ) {
                return;
            }

            // Then check if the users interacts with the inline node
            // in the editor. In this case, instantely cancel.
            const domNode = findDOMNode(node.key);
            assert(domNode);
            if (domNode.contains(event.target)) {
                onCancel?.();
                onEdit(false);
                return;
            }

            cancel({ focusEndOfInline: false });
        },
    });

    const handlePopoverHidden = () => {
        if (onExitedRef.current) {
            onExitedRef.current();
            onExitedRef.current = null;
        }
    };

    return {
        popoverRootProps: {
            triggerAction: 'hover' as const,
            // While editing, open and close must be synchronous.
            // Otherwise a delayed onOpenChange could fire after we exit edit mode.
            triggerDelay: isEditing ? 0 : 600,
            isOpen,
            onOpenChange: (isOpen) => {
                // In edit mode, the open state is fully controlled externally.
                // This relies on triggerDelay being 0, otherwise this callback
                // could run after edit mode has ended.
                if (!isEditing) {
                    onOpenChange(isOpen);
                }
            },
        },
        popoverProps: {
            ref: popoverRef,
            onHidden: handlePopoverHidden,
            isDismissable: false,
            onKeyDownCapture: (event: React.KeyboardEvent) => {
                if (event.key === 'Escape') {
                    event.stopPropagation();
                    cancel({ focusEndOfInline: true });
                }
            },
        },
        cancel,
        submit,
        edit,
    };
}

/**
 * Props to apply when a low-level inline tag is edited.
 */
export function useInlineTagProps(props: React.HTMLAttributes<HTMLElement>) {
    const isEditing = React.useContext(InlineEditingContext);
    if (isEditing) {
        return {
            ...props,
            className: clsx('-mx-1 rounded-sm bg-(--blue-4) px-1', props.className),
        };
    }
    return props;
}
